import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getFacilitatorDetail, FacilitatorDetail as Detail } from '../../services/api';
import MonitorLayout from './MonitorLayout';
import { MetricTile as Tile } from './MetricTile';
import { Period, periodRange, periodReady, describePeriod } from './period';
import PeriodPicker from './PeriodPicker';

/** A count (e.g. "18 absent") that reveals the student names in a popover on
 *  hover. Uses local state (not Tailwind group-hover, which isn't enabled here). */
const HoverNames: React.FC<{ label: string; names: string[]; color: string }> = ({ label, names, color }) => {
  const [open, setOpen] = useState(false);
  const hoverable = names.length > 0;
  return (
    <span
      className="relative"
      onMouseEnter={() => hoverable && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <span
        className={`${color} ${hoverable ? 'cursor-default underline decoration-dotted underline-offset-2' : ''}`}
        title={hoverable ? names.join(', ') : undefined}
      >
        {label}
      </span>
      {open && (
        <span className="absolute right-0 top-full z-20 mt-1 block w-64 whitespace-normal rounded-lg border border-[#E9E6F4] bg-white p-2.5 text-left text-[11px] font-normal leading-relaxed text-slate-600 shadow-lg">
          <span className="mb-1 block font-semibold text-slate-400">{names.length} student{names.length === 1 ? '' : 's'}</span>
          {names.join(', ')}
        </span>
      )}
    </span>
  );
};

const SUBJECT_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
  global: 'STEM / Your topics',
};
const subjectLabel = (s?: string | null): string => (s ? SUBJECT_LABELS[s] || s : 'STEM / Other');

const relLabel = (iso?: string | null): string => {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const days = Math.floor((Date.now() - t) / (24 * 60 * 60 * 1000));
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  return new Date(iso).toLocaleDateString();
};

const fmtMins = (m?: number | null): string => {
  if (!m) return '0m';
  const h = Math.floor(m / 60);
  const min = Math.round(m % 60);
  return h > 0 ? `${h}h ${min}m` : `${min}m`;
};

const classLabel = (d: Detail): string =>
  d.classes.length === 0 ? '—' : d.classes.map((c) => `${c.grade}${c.section ? `-${c.section}` : ''}`).join(', ');

const FacilitatorDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // ?school=... scopes the page to one of the facilitator's schools. Athira
  // (2026-08-26): with two or three schools under one facilitator, a single
  // attendance figure is meaningless, so attendance lives inside the school.
  const [sp] = useSearchParams();
  const school = sp.get('school') || undefined;
  const [d, setD] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attView, setAttView] = useState<'session' | 'class'>('session');
  // This page had no period filter, so every figure on it was all-time whatever
  // the dashboard said — that is why the same attendance showed under "this
  // week" and "all time" (Athira, 2026-08-19).
  const [period, setPeriod] = useState<Period>('all');
  const [customSince, setCustomSince] = useState<string | undefined>(undefined);
  const [customUntil, setCustomUntil] = useState<string | undefined>(undefined);

  useEffect(() => {
    const token = localStorage.getItem('monitorToken');
    if (!token) {
      navigate('/monitor/login', { replace: true });
      return;
    }
    if (!id) return;
    if (!periodReady(period, { since: customSince, until: customUntil })) {
      setLoading(false);
      return;
    }
    getFacilitatorDetail(
      token, Number(id), periodRange(period, { since: customSince, until: customUntil }), school,
    )
      .then(setD)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('monitorToken');
          navigate('/monitor/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [id, navigate, period, customSince, customUntil, school]);

  // Attendance is only readable for ONE school at a time. It stands up when the
  // page is scoped to a school, and also when the facilitator has just one —
  // then facilitator-wide and school-wide are the same number.
  const perSchool = d != null && (d.scope_school != null || d.schools.length <= 1);

  // Group completed topics by curriculum: subject -> chapter -> sub-topics.
  const topicsByCurriculum = useMemo(() => {
    const bySubject = new Map<string, Map<string, string[]>>();
    (d?.completed_topics ?? []).forEach((t) => {
      const s = subjectLabel(t.subject);
      const chapter = (t.chapter_title || '').trim() || '—';
      if (!bySubject.has(s)) bySubject.set(s, new Map());
      const chapters = bySubject.get(s)!;
      if (!chapters.has(chapter)) chapters.set(chapter, []);
      chapters.get(chapter)!.push(t.sub_topic);
    });
    return Array.from(bySubject.entries()).map(([subject, chapters]) => ({
      subject,
      chapters: Array.from(chapters.entries()).map(([chapter, subtopics]) => ({ chapter, subtopics })),
    }));
  }, [d]);

  return (
    <MonitorLayout>
      <button
        type="button"
        onClick={() => navigate(id ? `/monitor/facilitators/${id}` : '/monitor/facilitators')}
        className="mb-4 text-sm font-semibold text-slate-500 hover:text-slate-800"
      >
        ← Back
      </button>

      {loading ? (
        <p className="py-10 text-center text-sm text-slate-400">Loading…</p>
      ) : error || !d ? (
        <p className="py-10 text-center text-sm text-rose-600">{error || 'Not found.'}</p>
      ) : (
        <>
          {/* Header */}
          <div className="rounded-3xl bg-white px-6 py-5 shadow-sm">
            <div className="flex flex-wrap items-center gap-4">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-lg font-bold text-violet-700">
                {(d.name || d.username).slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900">{d.name || d.username}</h1>
                <p className="text-sm text-slate-500">
                  {/* Only name a single school when there IS one. A facilitator with
                      several has no "own" school, so the chips below carry them. */}
                  @{d.username}
                  {d.schools.length === 1 ? ` · ${d.schools[0]}` : ''}
                  {' · '}Classes {classLabel(d)}
                  {d.subjects.length ? ` · ${d.subjects.join(', ')}` : ''}
                </p>
                {d.scope_school ? (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-violet-100 px-2.5 py-0.5 font-bold text-violet-700">
                      {d.scope_school}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate(`/monitor/facilitators/${id}/detail`)}
                      className="font-semibold text-slate-400 underline decoration-dotted hover:text-slate-700"
                    >
                      show all {d.schools.length} school{d.schools.length === 1 ? '' : 's'}
                    </button>
                  </p>
                ) : d.schools.length > 1 ? (
                  <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
                    <span>Works in {d.schools.length} schools — open one for its attendance:</span>
                    {d.schools.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => navigate(`/monitor/facilitators/${id}/detail?school=${encodeURIComponent(s)}`)}
                        className="rounded-full bg-slate-100 px-2 py-0.5 font-semibold text-slate-600 hover:bg-violet-100 hover:text-violet-700"
                      >
                        {s}
                      </button>
                    ))}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-none items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-slate-400">Last active</p>
                  <p className="text-sm font-semibold text-slate-700">{relLabel(d.last_active)}</p>
                </div>
                <PeriodPicker
                  value={{ period, since: customSince, until: customUntil }}
                  onChange={(v) => { setPeriod(v.period); setCustomSince(v.since); setCustomUntil(v.until); }}
                />
              </div>
            </div>
          </div>

          <p className="mt-2 text-[11px] text-slate-400">
            Every figure below covers <strong>{describePeriod(period, { since: customSince, until: customUntil })}</strong>. "Last
            active" is always true recency, so inactivity stays visible.
            {d.scope_school
              ? ' Sessions, attendance, classes and module ticks are for this school only — those are the figures that belong to a school. Everything else is this facilitator\'s own work across every school they serve: coach time, active days, lesson plans and topics sent back for practice. Each lesson plan below shows which school it was for.'
              : ''}
          </p>

          {/* Activity tiles */}
          <div className="mt-4 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            {/* Athira asked on the 2026-08-19 call what "coach time" and
                "attendance" actually count, and nobody on the call could say.
                Every tile that isn't self-evident now carries the answer. */}
            <Tile
              value={fmtMins(d.coach_minutes)}
              label="Their own coach time"
              hint="Time this facilitator themselves spent on the coach — their own chats PLUS their time in the Planner building lesson plans. Not their students' time. Gaps over 5 minutes count as walking away. Planner time is recorded from 27 Aug 2026 onwards; planning done before that was never timestamped and cannot be recovered."
              tint="bg-violet-100 text-violet-700"
            />
            <Tile
              value={d.active_days ? `${Math.round(d.coach_minutes / d.active_days)}m` : '—'}
              label="Avg / active day"
              hint="On an active day, roughly how long they spent on the coach — their total coach time divided by their number of active days."
              tint="bg-sky-100 text-sky-700"
            />
            <Tile
              value={String(d.active_days)}
              label="Active days"
              hint="How many different days they were active in this period — a day counts if they had a coach chat, worked in the Planner, or ticked off module content. Planning that was never saved counts too, which it could not before. A login on its own never counts."
              tint="bg-emerald-100 text-emerald-700"
            />
            <Tile
              value={String(d.sessions_conducted)}
              label="Sessions conducted"
              hint="A session = teaching one class on one day, shown by them ticking off module content that day. Two classes taught on the same day count as two sessions."
              tint="bg-cyan-100 text-cyan-700"
            />
            <Tile
              value={d.attendance_pct == null ? '—' : `${d.attendance_pct}%`}
              label={
                perSchool
                  ? `Student attendance · ${d.students_enrolled} enrolled`
                  : `Student attendance · across ${d.schools.length} schools`
              }
              hint={`${
                d.scope_school
                  ? `${d.scope_school} only. `
                  : perSchool
                    ? ''
                    : `The average across all ${d.schools.length} schools this facilitator works in. Each school is worked out on its own — its students over its own session days — then the totals are added up and divided once, so a big school weighs more than a small one and no child is ever counted present at a class held in another school. Open a school above for its own figure. `
              }A 'session day' is a day this facilitator ticked off module content for a class (the app's only record that a class happened). A student counts as 'present' if they used the coach — had any chat — on one of those days. We add up the students present across every session day and divide by the total possible. Blank if they have no students or no sessions yet.`}
              tint="bg-rose-100 text-rose-700"
            />
            <Tile
              value={String(d.topics_ticked)}
              label="Topics completed"
              hint="Curriculum topics they ticked off as done with their class in this period."
              tint="bg-indigo-100 text-indigo-700"
            />
            <Tile
              value={String(d.lesson_plans_built)}
              label="Lesson plans"
              hint="Lesson plans they BUILT with the Planner in this period — one per module they asked activities for, counted once per module per day (Athira, 2026-08-26). A plan counts the moment it is built, whether or not they went on to save or push it; planning typed into the general chat is not counted."
              tint="bg-violet-100 text-violet-700"
            />
            <Tile
              value={String(d.reinforcements)}
              label="Topics sent back for practice"
              hint="Times they MANUALLY flagged a topic their class found hard, so the coach gives those students extra practice. This is the facilitator acting — separate from the struggle report the coach generates automatically. Likely low until facilitators are shown the flag."
              tint="bg-fuchsia-100 text-fuchsia-700"
            />
            <Tile
              value={String(d.assessments)}
              label="Assessments created"
              hint="Actual topic quizzes they generated in the app for their class this period — real product use."
              tint="bg-fuchsia-100 text-fuchsia-700"
            />
          </div>

          {/* Sessions conducted, by class — module-based (which grades, where) */}
          <div className="mt-4">
            <Card
              title="Sessions conducted"
              sub="A session = a class-day the facilitator covered module content (marked syllabus progress) — module-based, per class."
            >
              {d.sessions_by_class.length === 0 ? (
                <p className="text-sm text-slate-400">No module sessions recorded yet.</p>
              ) : (
                <div className="divide-y divide-[#F1EFFA]">
                  {d.sessions_by_class.map((s) => (
                    <div key={`${s.school}-${s.grade}-${s.section ?? ''}`} className="flex items-start justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800">{s.label} · {s.school}</p>
                        <p className="mt-0.5 text-xs text-slate-400">
                          {s.dates
                            .map((dt) => new Date(dt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }))
                            .join(' · ')}
                        </p>
                      </div>
                      <span className="flex-none rounded-full bg-cyan-100 px-2.5 py-0.5 text-xs font-bold text-cyan-700">
                        {s.sessions} session{s.sessions === 1 ? '' : 's'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Session-wise student attendance (provisional) */}
          <div className="mt-4">
            <Card
              title={d.scope_school ? `Student attendance · ${d.scope_school}` : 'Student attendance'}
              sub={
                perSchool
                  ? "Provisional — a student is 'present' if active on a session day (a day the facilitator covered module content); final rule pending VU."
                  : "Provisional — a student is 'present' if active on a session day. Sessions from every school this facilitator serves are listed below, each tagged with its school."
              }
            >
              <div className="mb-3 inline-flex rounded-lg bg-slate-100 p-0.5 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => setAttView('session')}
                  className={`rounded-md px-3 py-1 ${attView === 'session' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500'}`}
                >
                  By session
                </button>
                <button
                  type="button"
                  onClick={() => setAttView('class')}
                  className={`rounded-md px-3 py-1 ${attView === 'class' ? 'bg-white text-violet-700 shadow-sm' : 'text-slate-500'}`}
                >
                  By class
                </button>
              </div>

              {attView === 'session' ? (
                d.session_attendance.length === 0 ? (
                  <p className="text-sm text-slate-400">No sessions recorded yet.</p>
                ) : (
                  <div className="space-y-3">
                    {d.session_attendance.map((sess) => (
                      <div key={`${sess.school ?? ''}-${sess.session_date}`} className="flex items-center justify-between rounded-xl border border-[#F1EFFA] px-3 py-2.5">
                        <span className="text-sm font-semibold text-slate-800">
                          {new Date(sess.session_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                          {sess.school ? (
                            <span className="ml-2 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
                              {sess.school}
                            </span>
                          ) : null}
                        </span>
                        <span className="text-xs font-semibold">
                          <HoverNames label={`${sess.present} present`} names={sess.present_names} color="text-emerald-600" />
                          <span className="text-slate-300"> · </span>
                          <HoverNames label={`${sess.absent} absent`} names={sess.absent_names} color="text-rose-600" />
                        </span>
                      </div>
                    ))}
                  </div>
                )
              ) : d.class_attendance.length === 0 ? (
                <p className="text-sm text-slate-400">No classes assigned.</p>
              ) : (
                <div className="space-y-4">
                  {(() => {
                    const groups = new Map<string, typeof d.class_attendance>();
                    d.class_attendance.forEach((c) => {
                      const arr = groups.get(c.school) || [];
                      arr.push(c);
                      groups.set(c.school, arr);
                    });
                    return Array.from(groups.entries()).map(([school, classes]) => (
                      <div key={school}>
                        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-violet-500">{school}</p>
                        <div className="space-y-2">
                          {classes.map((c) => (
                            <div key={c.label} className="flex items-center justify-between rounded-xl border border-[#F1EFFA] px-3 py-2.5">
                              <span className="text-sm font-semibold text-slate-800">
                                {c.label}
                                <span className="ml-1.5 text-xs font-normal text-slate-400">· {c.enrolled} students</span>
                              </span>
                              <span className="text-sm font-bold text-slate-700">
                                {c.attendance_pct == null ? '—' : `${c.attendance_pct}%`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </Card>
          </div>

          {d.completed_topics.length > 0 && (
            <div className="mt-4">
              <Card title="Topics completed" sub="By curriculum — subject → chapter → sub-topics">
                <div className="space-y-4">
                  {topicsByCurriculum.map((g) => (
                    <div key={g.subject}>
                      <p className="text-[11px] font-bold uppercase tracking-wide text-violet-500">{g.subject}</p>
                      <div className="mt-1.5 space-y-2">
                        {g.chapters.map((c) => (
                          <div key={c.chapter}>
                            {c.chapter !== '—' && (
                              <p className="mb-1 text-xs font-semibold text-slate-600">{c.chapter}</p>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {c.subtopics.map((st, i) => (
                                <span
                                  key={i}
                                  className="rounded-full bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800"
                                >
                                  {st}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Full width, not a two-up grid: the "What they asked the coach" list
              that used to sit beside this is gone (2026-08-20) — the transcripts
              had already been dropped as "like reading someone else's ChatGPT"
              (Athira, 2026-08-19), and the bare chat titles said no more than the
              chat count already on the tiles above. */}
          <div className="mt-4">
            <Card
              title="Lesson plans"
              sub="Every plan they built, newest first — generated, saved or pushed to the class"
            >
              {d.lesson_plans.length === 0 ? (
                <Empty>No lesson plans yet.</Empty>
              ) : (
                <div className="divide-y divide-[#F1EFFA]">
                  {d.lesson_plans.map((lp, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800">{lp.title}</p>
                        <p className="text-xs text-slate-400">
                          {lp.grade != null ? `Grade ${lp.grade}${lp.section ? `-${lp.section}` : ''}` : ''}
                          {lp.school ? ` · ${lp.school}` : ''}
                          {lp.subject ? ` · ${lp.subject}` : ''}
                          {lp.updated_at ? ` · ${new Date(lp.updated_at).toLocaleDateString()}` : ''}
                        </p>
                      </div>
                      <span
                        className={`flex-none rounded-full px-2.5 py-0.5 text-[0.68rem] font-bold ${
                          lp.status === 'pushed'
                            ? 'bg-emerald-100 text-emerald-700'
                            : lp.status === 'draft'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-500'
                        }`}
                        title={
                          lp.status === 'pushed'
                            ? 'Saved as the class plan and pushed to the children.'
                            : lp.status === 'draft'
                              ? "Saved as the class's current plan, not pushed."
                              : 'Built in the Planner. Never saved to the class — the work still counts.'
                        }
                      >
                        {lp.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </MonitorLayout>
  );
};

const Card: React.FC<{ title: string; sub?: string; children: React.ReactNode }> = ({ title, sub, children }) => (
  <div className="rounded-2xl border border-[#E9E6F4] bg-white p-5">
    <h2 className="text-sm font-bold text-slate-900">{title}</h2>
    {sub ? <p className="mb-2 text-xs text-slate-500">{sub}</p> : null}
    <div className={sub ? '' : 'mt-2'}>{children}</div>
  </div>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="py-4 text-sm text-slate-400">{children}</p>
);

export default FacilitatorDetailPage;
